export const DRAWING_TOOL_STATUS = Object.freeze({
  AVAILABLE: "available",
  PLANNED: "planned",
});

export const DRAWING_TOOLS = Object.freeze([
  {
    id: "trend-line",
    name: "Trend Line",
    status: DRAWING_TOOL_STATUS.AVAILABLE,
    pointsRequired: 2,
    description: "Two-point directional line anchored to chart coordinates.",
  },
  {
    id: "horizontal-line",
    name: "Horizontal Line",
    status: DRAWING_TOOL_STATUS.AVAILABLE,
    pointsRequired: 1,
    description: "Price-level reference line anchored to one chart price.",
  },
  {
    id: "vertical-line",
    name: "Vertical Line",
    status: DRAWING_TOOL_STATUS.AVAILABLE,
    pointsRequired: 1,
    description: "Time reference line anchored to one chart timestamp.",
  },
  {
    id: "ray",
    name: "Ray",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Directional line extending from an anchor through a second point.",
  },
  {
    id: "arrow",
    name: "Arrow",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Directional annotation between two chart coordinates.",
  },
  {
    id: "text",
    name: "Text / Annotation",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 1,
    description: "Chart note anchored to a time and price coordinate.",
  },
  {
    id: "callout",
    name: "Callout",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 1,
    description: "Future labeled chart callout anchored to a time and price coordinate.",
  },
  {
    id: "rectangle",
    name: "Rectangle",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Price/time zone bounded by two chart coordinates.",
  },
  {
    id: "ellipse",
    name: "Ellipse",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Elliptical chart annotation bounded by chart coordinates.",
  },
  {
    id: "channel",
    name: "Channel",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 3,
    description: "Parallel trend channel defined by chart anchors.",
  },
  {
    id: "measure",
    name: "Measure",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Measure time, price, and percentage distance between chart points.",
  },
  {
    id: "fibonacci-retracement",
    name: "Fibonacci Retracement",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Retracement levels derived from two user-selected chart anchors.",
  },
  {
    id: "fibonacci-extension",
    name: "Fibonacci Extension",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 3,
    description: "Future extension levels derived from user-selected chart anchors.",
  },
  {
    id: "regression-channel",
    name: "Regression Channel",
    status: DRAWING_TOOL_STATUS.PLANNED,
    pointsRequired: 2,
    description: "Future statistical channel over a user-selected authenticated bar range.",
  },
]);

export function getDrawingTool(toolId) {
  return DRAWING_TOOLS.find((tool) => tool.id === toolId) || null;
}

export function canUseDrawingTool(toolId) {
  return getDrawingTool(toolId)?.status === DRAWING_TOOL_STATUS.AVAILABLE;
}

export function createDrawing({ id, paneId, toolId, points = [], metadata = {} }) {
  if (!id) throw new Error("A drawing id is required.");
  if (!paneId) throw new Error("A pane id is required.");
  if (!canUseDrawingTool(toolId)) throw new Error("Drawing tool is not available.");

  return {
    id,
    paneId,
    toolId,
    points: points.map((point) => ({ ...point })),
    metadata: { ...metadata },
    isLocked: false,
    isVisible: true,
    isSelected: false,
  };
}

export function drawingReducer(drawings, action) {
  switch (action.type) {
    case "ADD_DRAWING":
      return [...drawings, createDrawing(action.drawing)];

    case "UPDATE_DRAWING":
      return drawings.map((drawing) => {
        if (drawing.id !== action.drawingId || drawing.isLocked) return drawing;
        return {
          ...drawing,
          ...action.patch,
          points: action.patch?.points
            ? action.patch.points.map((point) => ({ ...point }))
            : drawing.points,
          metadata: action.patch?.metadata
            ? { ...drawing.metadata, ...action.patch.metadata }
            : drawing.metadata,
        };
      });

    case "SELECT_DRAWING":
      return drawings.map((drawing) => ({
        ...drawing,
        isSelected: drawing.id === action.drawingId,
      }));

    case "TOGGLE_DRAWING_LOCK":
      return drawings.map((drawing) =>
        drawing.id === action.drawingId
          ? { ...drawing, isLocked: !drawing.isLocked }
          : drawing
      );

    case "TOGGLE_DRAWING_VISIBILITY":
      return drawings.map((drawing) =>
        drawing.id === action.drawingId
          ? { ...drawing, isVisible: !drawing.isVisible }
          : drawing
      );

    case "DELETE_DRAWING":
      return drawings.filter((drawing) => drawing.id !== action.drawingId);

    case "CLEAR_PANE_DRAWINGS":
      return drawings.filter((drawing) => drawing.paneId !== action.paneId);

    default:
      return drawings;
  }
}
